/**
 * System Diagnostics and Health Monitoring Hooks
 * 
 * Provides real-time system health monitoring and diagnostic capabilities for
 * administrators. Tracks the health of critical system components, business
 * metrics, and enables on-demand diagnostic testing.
 * 
 * **Available Hooks:**
 * 1. `useSystemHealth` - Monitor system component health
 * 2. `useBusinessMetrics` - Track key business performance indicators
 * 3. `useSystemDiagnostics` - Run diagnostic tests and generate reports
 * 
 * **System Health Components:**
 * - **Database**: Connection, query performance, connection pool
 * - **Storage**: S3 connectivity, bucket access, file operations
 * - **Email**: SMTP configuration, delivery status, queue health
 * - **Scheduler**: Cron job execution, background task processing
 * - **Overall**: Aggregated system status
 * 
 * **Health Status Levels:**
 * - `healthy`: All checks passing, normal operation
 * - `degraded`: Some non-critical issues, system functional
 * - `unhealthy`: Critical issues, service disruption
 * 
 * **Business Metrics (Last 24 Hours):**
 * - Jobs created
 * - RFQs opened
 * - Bids submitted
 * - Awards made
 * - Completions
 * - Disputes opened
 * 
 * **Diagnostic Test Types:**
 * - `email`: Test SMTP configuration and delivery
 * - `storage`: Test S3 connectivity and operations
 * - `database`: Test connection and query performance
 * - `all`: Run comprehensive system check
 * 
 * **Auto-Refresh:**
 * - Health status refreshes every 30 seconds
 * - Business metrics refresh every 5 minutes
 * - Manual refresh available via `refetch`
 * 
 * **Use Cases:**
 * - Admin dashboard health monitoring
 * - Incident response and troubleshooting
 * - Performance trend analysis
 * - Pre-deployment health checks
 * - Automated alerting triggers
 * 
 * @example
 * // Monitor system health
 * const { health, loading, refetch } = useSystemHealth();
 * 
 * if (health?.overall === 'unhealthy') {
 *   return <CriticalAlert>System is down!</CriticalAlert>;
 * }
 * 
 * @example
 * // Track business metrics
 * const { metrics } = useBusinessMetrics();
 * 
 * <MetricCard>
 *   <h3>Jobs Created (24h)</h3>
 *   <p>{metrics?.jobs_created_24h}</p>
 * </MetricCard>
 * 
 * @example
 * // Run diagnostics
 * const { runDiagnostic, generateSystemReport } = useSystemDiagnostics();
 * 
 * // Test specific component
 * const emailTest = await runDiagnostic('email', 'admin@example.com');
 * 
 * // Generate full report
 * const report = await generateSystemReport();
 * console.log(report.metrics, report.recentErrors);
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';

export interface SystemHealth {
  database: 'healthy' | 'degraded' | 'unhealthy';
  storage: 'healthy' | 'degraded' | 'unhealthy';
  email: 'healthy' | 'degraded' | 'unhealthy';
  scheduler: 'healthy' | 'degraded' | 'unhealthy';
  overall: 'healthy' | 'degraded' | 'unhealthy';
  last_updated: string;
}


export interface BusinessMetrics {
  jobs_created_24h: number;
  rfqs_opened_24h: number;
  bids_submitted_24h: number;
  awards_made_24h: number;
  completions_24h: number;
  disputes_opened_24h: number;
}

export interface ErrorLog {
  id: string;
  timestamp: string;
  level: 'error' | 'warning' | 'info';
  component: string;
  message: string;
  user_id?: string;
  details?: any;
}

export const useSystemHealth = () => {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = async () => {
    try {
      setLoading(true);
      const { data, error: healthError } = await invokeEdgeFunction('health-check', {
        callerOperation: 'useSystemHealth.fetchHealth',
      });
      
      if (healthError) throw healthError;

      const healthResponse = data as {
        status?: string;
        checks?: {
          database?: { status?: string };
          storage?: { status?: string };
          email?: { status?: string };
          scheduler?: { status?: string };
        };
      };

      const healthData: SystemHealth = {
        database: (healthResponse.checks?.database?.status as SystemHealth['database']) || 'unknown' as any,
        storage: (healthResponse.checks?.storage?.status as SystemHealth['storage']) || 'unknown' as any,
        email: (healthResponse.checks?.email?.status as SystemHealth['email']) || 'unknown' as any,
        scheduler: (healthResponse.checks?.scheduler?.status as SystemHealth['scheduler']) || 'unknown' as any,
        overall: (healthResponse.status as SystemHealth['overall']) || 'unknown' as any,
        last_updated: new Date().toISOString()
      };

      setHealth(healthData);
      setError(null);
    } catch (err) {
      console.error('Error fetching system health:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  return { health, loading, error, refetch: fetchHealth };
};

interface UseBusinessMetricsOptions {
  includeTestData?: boolean;
}

export const useBusinessMetrics = (options: UseBusinessMetricsOptions = {}) => {
  const { includeTestData = false } = options;
  const [metrics, setMetrics] = useState<BusinessMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBusinessMetrics = async () => {
    try {
      setLoading(true);
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      // Get test organization IDs to exclude if not including test data
      let testOrgIds: string[] = [];
      if (!includeTestData) {
        const { data: testOrgs } = await supabase
          .from('organizations')
          .select('id')
          .eq('is_test', true);
        testOrgIds = testOrgs?.map(o => o.id) || [];
      }

      // Build queries with optional test data filtering
      let jobsQuery = supabase.from('jobs').select('id').gte('created_at', yesterday);
      let rfqsQuery = supabase.from('rfqs').select('id, jobs!inner(organization_id)').gte('created_at', yesterday);
      let bidsQuery = supabase.from('bids').select('id, rfqs!inner(jobs!inner(organization_id))').gte('created_at', yesterday);
      let awardsQuery = supabase.from('awards').select('id, jobs!inner(organization_id)').gte('created_at', yesterday);
      let completionsQuery = supabase.from('job_completions').select('id, organization_id').gte('created_at', yesterday);
      let disputesQuery = supabase.from('disputes').select('id').gte('created_at', yesterday);

      if (testOrgIds.length > 0) {
        jobsQuery = jobsQuery.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
        disputesQuery = disputesQuery.not('organization_id', 'in', `(${testOrgIds.join(',')})`);
      }

      const [jobs, rfqs, bids, awards, completions, disputes] = await Promise.all([
        jobsQuery,
        rfqsQuery,
        bidsQuery,
        awardsQuery,
        completionsQuery,
        disputesQuery,
      ]);

      // Filter related entities by test org (needed for joined queries)
      let filteredRfqs = rfqs.data || [];
      let filteredBids = bids.data || [];
      let filteredAwards = awards.data || [];
      let filteredCompletions = completions.data || [];

      if (testOrgIds.length > 0) {
        filteredRfqs = filteredRfqs.filter((r: any) => !testOrgIds.includes(r.jobs?.organization_id));
        filteredBids = filteredBids.filter((b: any) => !testOrgIds.includes(b.rfqs?.jobs?.organization_id));
        filteredAwards = filteredAwards.filter((a: any) => !testOrgIds.includes(a.jobs?.organization_id));
        filteredCompletions = filteredCompletions.filter((c: any) => !testOrgIds.includes(c.organization_id));
      }

      const businessMetrics: BusinessMetrics = {
        jobs_created_24h: jobs.data?.length || 0,
        rfqs_opened_24h: filteredRfqs.length,
        bids_submitted_24h: filteredBids.length,
        awards_made_24h: filteredAwards.length,
        completions_24h: filteredCompletions.length,
        disputes_opened_24h: disputes.data?.length || 0
      };

      setMetrics(businessMetrics);
      setError(null);
    } catch (err) {
      console.error('Error fetching business metrics:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinessMetrics();
    const interval = setInterval(fetchBusinessMetrics, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, [includeTestData]);

  return { metrics, loading, error, refetch: fetchBusinessMetrics };
};

export const useSystemDiagnostics = () => {
  const runDiagnostic = async (testType: 'email' | 'storage' | 'database' | 'all', adminEmail?: string) => {
    try {
      const { data, error } = await invokeEdgeFunction('system-diagnostics', {
        body: { test_type: testType, admin_email: adminEmail },
        callerOperation: 'useSystemDiagnostics.runDiagnostic',
      });

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error running diagnostic:', err);
      throw err;
    }
  };

  const generateSystemReport = async () => {
    try {
      const { data, error } = await invokeEdgeFunction('system-report', {
        body: { include_metrics: true, include_logs: true, time_range_hours: 24 },
        callerOperation: 'useSystemDiagnostics.generateSystemReport',
      });

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error generating system report:', err);
      throw err;
    }
  };

  const clearCache = async () => {
    // In a real implementation, this would clear application caches
    // For now, we'll just simulate the action
    return new Promise(resolve => setTimeout(resolve, 1000));
  };

  return {
    runDiagnostic,
    generateSystemReport,
    clearCache
  };
};